import { useEffect } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export const THEME_PREFERENCES = ['system', 'light', 'dark', 'high-contrast'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]
export type AppliedTheme = Exclude<ThemePreference, 'system'>

interface ThemeState {
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'system',
      setTheme: (theme) => {
        set({ theme })
      },
    }),
    {
      name: 'app:theme',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ theme: state.theme }),
    },
  ),
)

const DARK_QUERY = '(prefers-color-scheme: dark)'

function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

export function resolveTheme(
  preference: ThemePreference,
  systemDark: boolean = prefersDark(),
): AppliedTheme {
  if (preference !== 'system') return preference
  return systemDark ? 'dark' : 'light'
}

/** Sets `data-theme` on <html>. Called before first render to avoid a flash of the wrong theme. */
export function applyTheme(
  preference: ThemePreference,
  root: HTMLElement = document.documentElement,
): AppliedTheme {
  const applied = resolveTheme(preference)
  root.dataset.theme = applied
  return applied
}

/** Applies the persisted theme and follows OS changes while the preference is `system`. */
export function ThemeSync() {
  const theme = useThemeStore((state) => state.theme)

  useEffect(() => {
    applyTheme(theme)
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return
    const media = window.matchMedia(DARK_QUERY)
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => {
      media.removeEventListener('change', onChange)
    }
  }, [theme])

  return null
}

export function isThemePreference(value: string): value is ThemePreference {
  return (THEME_PREFERENCES as readonly string[]).includes(value)
}
